import { Controller } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  Typography as T,
} from "@mui/material";
import FileTree from "files/components/FileTree";
import { JobType } from "files/types/interfaces/fileJob";
import TextOverflow from "~/components/TextOverflow";
import { useFileActionDialog } from "~/hooks/useFileActionDialog";

interface ModalProps {
  open: boolean;
  action: JobType;
  isDirectory: boolean;
  handleClose: () => void;
  fileKey: string;
  /**
   * Explicit directory prefix to revalidate after the action completes. Pass this for
   * callers (like PersonalDatasetFiles) that track the browsed folder as local state
   * rather than the URL.
   */
  prefix?: string;
}

const ModalBody = ({ handleClose, action, isDirectory, fileKey, prefix }: ModalProps) => {
  const {
    filesData,
    selectedFile,
    setSelectedFile,
    fileSystemNodeName,
    handleSubmit,
    submitDisabled,
    submitting,
    originalName,
    form,
    inputRef,
    error,
  } = useFileActionDialog({ action, fileKey, isDirectory, handleClose, prefix });

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <DialogTitle>
        <TextOverflow
          sx={{
            "&::first-letter": {
              textTransform: "capitalize",
            },
          }}
        >
          {action === JobType.RENAME
            ? `${action} ${fileSystemNodeName}`
            : `${action} ${fileSystemNodeName} to...`}
        </TextOverflow>
      </DialogTitle>
      <DialogContent sx={{ pb: 0, gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {action === JobType.RENAME ? (
          <FormControl>
            <Controller
              control={form.control}
              name="fileSystemNodeName"
              rules={{
                required: { value: true, message: "Required" },
                validate: {
                  newName: (value: string) => {
                    if (value === originalName) {
                      return "Please enter a new name";
                    }
                  },
                  validName: (value: string) => {
                    if (value.includes("/")) {
                      return `${fileSystemNodeName} name cannot contain "/"`;
                    }
                  },
                  uniqueName: (value: string) => {
                    // Get keys of existing subdirectories
                    const siblings = filesData?.children?.map((file) => file.key);

                    const objectKey = `${filesData?.current_file?.key}${value}${
                      isDirectory ? "/" : ""
                    }`;

                    if (siblings?.includes(objectKey)) {
                      return `${fileSystemNodeName} already exists`;
                    }
                  },
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <FormLabel htmlFor={field.name}>
                    <T variant="label">New Name *</T>
                  </FormLabel>
                  <OutlinedInput
                    {...field}
                    autoComplete="off"
                    autoFocus
                    disabled={submitting}
                    error={!!error}
                    fullWidth
                    id={field.name}
                    inputRef={inputRef}
                    size="small"
                  />
                  <FormHelperText error>{error?.message ?? " "}</FormHelperText>
                </>
              )}
            />
          </FormControl>
        ) : (
          <FileTree
            requiredAccess="write"
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          loading={submitting}
          disabled={action === JobType.RENAME ? !form.formState.isValid : submitDisabled}
          variant="contained"
          size="medium"
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};

const FileActionDialog = (props: ModalProps) => (
  <Dialog
    fullWidth
    open={props.open}
    onClose={props.handleClose}
    /* Workaround to handle autoFocus in dev mode */
    disableRestoreFocus={import.meta.env.DEV}
  >
    <ModalBody {...props} />
  </Dialog>
);

export default FileActionDialog;
